/**
 * tetto — il tetto di spesa EXTRA di uno strumento a canone (atlante cap. 251, tappa 3; decisione di Simone del
 * 07/10/2026: si parte da un tetto pari al canone, e l'azienda lo alza, lo abbassa o lo toglie). Per i servizi che si
 * pagano solo a consumo (tappa 5) l'extra è tutta la spesa, e il tetto di partenza è un numero del listino (20 €).
 *
 * Dove sta: `billing.tetti_extra_eur.<capability>` sul documento dell'azienda, scritto SOLO dal server
 * (`impostaTettoStrumento`): assente = il predefinito (`tetto_predefinito_eur` del listino, o il canone dello
 * strumento); un numero = quel tetto in euro; `null` = nessun tetto, scelto dall'azienda.
 *
 * Cosa succede al tetto lo decide chi usa lo strumento, non questa regola: l'agente WhatsApp smette di scrivere di sua
 * iniziativa (le risposte a chi gli scrive continuano), il centralino non si ferma mai e avvisa soltanto, le misure SEO
 * e le analisi di Yuma non partono più (prima di partire prenotano la stima, `stimaNelTetto`).
 *
 * Nucleo PURO, condiviso fra gestionale (pagina dell'abbonamento, Yuma) e server.
 */

import { extraInCentesimi, strumentoPerLAzienda, type ConsumoDelloStrumento, type StrumentoACanone } from './listino';

export const TETTO_MASSIMO_EUR = 10_000;

export type TettoDelloStrumento =
  | { readonly tipo: 'predefinito'; readonly eur: number }
  | { readonly tipo: 'scelto'; readonly eur: number }
  | { readonly tipo: 'nessuno' };

/**
 * Il tetto di partenza in euro per QUESTA azienda: quello del listino se c'è (i servizi senza canone), altrimenti il
 * canone che l'azienda paga davvero (una versione di prezzo precedente, o su misura, se ce l'ha).
 */
export function tettoPredefinitoEur(datiAzienda: unknown, listino: StrumentoACanone): number {
  return listino.tetto_predefinito_eur ?? strumentoPerLAzienda(datiAzienda, listino).canone_mensile_eur;
}

export function tettoDelloStrumento(datiAzienda: unknown, listino: StrumentoACanone): TettoDelloStrumento {
  const strumento = strumentoPerLAzienda(datiAzienda, listino);
  const predefinito = tettoPredefinitoEur(datiAzienda, listino);
  const billing = ((datiAzienda as { billing?: unknown } | null | undefined)?.billing ?? {}) as Record<string, unknown>;
  const tetti = (billing.tetti_extra_eur && typeof billing.tetti_extra_eur === 'object' ? billing.tetti_extra_eur : {}) as Record<string, unknown>;
  if (!Object.prototype.hasOwnProperty.call(tetti, strumento.capability)) {
    return { tipo: 'predefinito', eur: predefinito };
  }
  const v = tetti[strumento.capability];
  if (v === null) return { tipo: 'nessuno' };
  if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return { tipo: 'scelto', eur: v };
  // Un valore storto non toglie la protezione: vale il predefinito.
  return { tipo: 'predefinito', eur: predefinito };
}

/**
 * Il tetto è raggiunto? Sì quando la PROSSIMA unità (un messaggio, un minuto) porterebbe l'extra oltre il tetto:
 * con 69 € e 0,15 € a messaggio lo si tocca a 660 messaggi (460 oltre i 200 compresi, 69,00 € esatti); con un
 * tetto a 0 € alla fine della quota compresa, che resta tutta usabile. Si ragiona in unità e non sull'extra già
 * maturato perché a extra zero «quota appena finita» e «quota appena cominciata» sarebbero indistinguibili.
 * Senza tetto, o con un extra che non costa niente, mai.
 */
export function tettoRaggiunto(consumo: ConsumoDelloStrumento, unitaConsumate: number, tetto: TettoDelloStrumento): boolean {
  if (tetto.tipo === 'nessuno') return false;
  return extraInCentesimi(consumo, Math.floor(unitaConsumate) + 1) > Math.round(tetto.eur * 100);
}

/**
 * Ci sta ancora `unitaInPiu` (una stima) sotto il tetto, dopo le `unitaGiaContate` del mese (consumate più già
 * prenotate)? Per i servizi che PRIMA di partire prenotano la loro stima (misure SEO, analisi di Yuma): con 20 € di
 * tetto, 19,50 € già spesi e una misura stimata 1 €, no. Senza tetto, sempre.
 */
export function stimaNelTetto(consumo: ConsumoDelloStrumento, unitaGiaContate: number, unitaInPiu: number, tetto: TettoDelloStrumento): boolean {
  if (tetto.tipo === 'nessuno') return true;
  return extraInCentesimi(consumo, Math.floor(unitaGiaContate) + Math.ceil(unitaInPiu)) <= Math.round(tetto.eur * 100);
}

/** Pura: un valore richiesto per il tetto è accettabile? null = nessun tetto. Il motivo è per chi chiede. */
export function validaTetto(valore: unknown): { ok: true; eur: number | null } | { ok: false; motivo: 'non_numero' | 'negativo' | 'troppo_alto' } {
  if (valore === null) return { ok: true, eur: null };
  if (typeof valore !== 'number' || !Number.isFinite(valore)) return { ok: false, motivo: 'non_numero' };
  if (valore < 0) return { ok: false, motivo: 'negativo' };
  if (valore > TETTO_MASSIMO_EUR) return { ok: false, motivo: 'troppo_alto' };
  return { ok: true, eur: Math.round(valore * 100) / 100 };
}
